import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/api';
import type { Challenge, ChallengeTierConfig, ChallengeSnapshot, ChallengeTier, LeaderboardEntry } from '@/types/api';

export function useChallenges(status?: string) {
  return useQuery<Challenge[]>({
    queryKey: ['challenges', status],
    queryFn: async () => {
      const params = status ? `?status=${status}` : '';
      const res = await api.get(`/challenges${params}`);
      return res.data;
    },
  });
}

export function useChallenge(id: string) {
  return useQuery<Challenge>({
    queryKey: ['challenges', id],
    queryFn: async () => {
      const res = await api.get(`/challenges/${id}`);
      return res.data;
    },
    enabled: !!id,
    refetchInterval: 5000,
  });
}

export function useChallengeTiers() {
  return useQuery<Record<ChallengeTier, ChallengeTierConfig>>({
    queryKey: ['challenge-tiers'],
    queryFn: async () => {
      const res = await api.get('/challenges/tiers');
      return res.data;
    },
    staleTime: 60_000,
  });
}

export function useChallengeSnapshots(challengeId: string) {
  return useQuery<ChallengeSnapshot[]>({
    queryKey: ['challenge-snapshots', challengeId],
    queryFn: async () => {
      const res = await api.get(`/challenges/${challengeId}/snapshots`);
      return res.data;
    },
    enabled: !!challengeId,
  });
}

export function useLeaderboard(limit = 20) {
  return useQuery<LeaderboardEntry[]>({
    queryKey: ['challenge-leaderboard', limit],
    queryFn: async () => {
      const res = await api.get(`/challenges/leaderboard?limit=${limit}`);
      return res.data;
    },
    refetchInterval: 30_000,
  });
}

export function useCreateChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tier: ChallengeTier) => {
      const res = await api.post('/challenges', { tier });
      return res.data as Challenge;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['challenges'] });
      toast.success('Challenge created');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to create challenge');
    },
  });
}

export function useCancelChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (challengeId: string) => {
      const res = await api.post(`/challenges/${challengeId}/cancel`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['challenges'] });
      toast.success('Challenge cancelled');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to cancel challenge');
    },
  });
}

export function useActivateChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      challengeId,
      strategyId,
      mt5AccountId,
      symbol,
    }: {
      challengeId: string;
      strategyId: string;
      mt5AccountId?: string;
      symbol?: string;
    }) => {
      const res = await api.post(`/challenges/${challengeId}/activate`, {
        strategyId,
        mt5AccountId,
        symbol,
      });
      return res.data as Challenge;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['challenges'] });
      toast.success('Challenge activated! The clock is ticking.');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to activate challenge');
    },
  });
}
